import {createContext, memo, useContext, useMemo} from 'react'
import invariant from 'tiny-invariant'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {useFilteredItems} from '../../features/filtering/hooks/use-filtered-items'
import {groupItems, type GroupItemsResult} from '../../features/grouping/group-items'
import {buildGroupingConfiguration} from '../../features/grouping/grouping-metadata-configurations'
import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'

export type SlicerItemsContextValue = {
  slicerItems: Array<GroupItemsResult<MemexItemModel>> | null
}

const context = createContext<SlicerItemsContextValue>({slicerItems: null})

export const useSlicerItems = () => {
  const contextValue = useContext(context)

  return contextValue
}

const groupingConfiguration = buildGroupingConfiguration((item: MemexItemModel) => item.columns, {
  useDistinctAssigneesGrouping: true,
})

export const SlicerItemsProvider = memo<{children: React.ReactNode}>(function SlicerItemsProvider({children}) {
  const sliceBy = useSliceBy()
  const {items: allItems} = useMemexItems()
  const {filteredItems} = useFilteredItems({applyTransientFilter: 'exclude'})

  const slicerItems = useMemo(() => {
    if (!sliceBy.sliceField || sliceBy.sliceField.dataType === MemexColumnDataType.TrackedBy) return []
    const groupForColumn = groupingConfiguration[sliceBy.sliceField.dataType]
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    invariant(groupForColumn, `No grouping configuration for column ${sliceBy.sliceField}`)

    return groupItems(
      filteredItems,
      sliceBy.sliceField,
      groupForColumn.getGroupingMetadata,
      groupForColumn.getAllGroupMetadata,
      undefined,
      allItems,
    )
  }, [allItems, filteredItems, sliceBy.sliceField])

  const ctx = useMemo(
    () => ({
      slicerItems,
    }),

    [slicerItems],
  )

  return <context.Provider value={ctx}>{children}</context.Provider>
})

try{ SlicerItemsProvider.displayName ||= 'SlicerItemsProvider' } catch {}